import { Fragment, useState } from 'react';
import { AdjustmentRequestsPanel } from './AdjustmentRequestsPanel';
import { toast } from '@/hooks/useToast';
import { ProductSheet } from './ProductSheet';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { ApiError } from '@/lib/api/client';
import { useAuth } from '@/hooks/useAuth';
import { ScheduleBadge } from './ScheduleBadge';
import InventoryUploadDialog from './InventoryUploadDialog';
import { useInventory } from './useInventory';
import { StorePicker } from './StorePicker';
import { useHasMultipleStores } from './usePharmacyStores';
import { useCreateStockAdjustment } from './useCreateStockAdjustment';
import { useInventoryHistory } from './useInventoryHistory';
import type { AdjustmentType, InventoryItem, InventoryMovement } from './types';

const MOVEMENT_META: Record<InventoryMovement['type'], { icon: string; label: string }> = {
  receipt: { icon: '📥', label: 'Goods receipt' },
  dispense: { icon: '📤', label: 'Dispensed' },
  adjustment: { icon: '⚖️', label: 'Adjustment' },
};

/** Only queries once expanded — see the "History" toggle below. */
function InventoryHistoryPanel({ inventoryId }: { inventoryId: number }) {
  const historyQuery = useInventoryHistory(inventoryId);
  const movements = historyQuery.data ?? [];
  if (historyQuery.isLoading) return <div className="p-3 text-center text-xs text-ink-muted">Loading history…</div>;
  if (!movements.length) return <div className="p-3 text-center text-xs text-ink-muted">No recorded movement for this batch yet.</div>;
  return (
    <div className="p-3">
      <Table>
        <TableHeader>
          <TableRow>{['When', 'Movement', 'Qty', 'Reference', 'Detail', 'By'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
        </TableHeader>
        <TableBody>
          {movements.map((m, i) => {
            const meta = MOVEMENT_META[m.type];
            return (
              <TableRow key={i}>
                <TableCell className="whitespace-nowrap text-xs text-ink-muted">{new Date(m.at).toLocaleString('en-IN')}</TableCell>
                <TableCell>{meta.icon} {meta.label}</TableCell>
                <TableCell className={`font-bold ${m.quantity_delta < 0 ? 'text-danger' : 'text-success'}`}>
                  {m.quantity_delta > 0 ? '+' : ''}{m.quantity_delta}
                </TableCell>
                <TableCell className="font-mono text-xs">{m.reference ?? '—'}</TableCell>
                <TableCell className="text-xs text-ink-muted">{m.detail ?? '—'}</TableCell>
                <TableCell className="text-xs text-ink-muted">{m.actor ?? '—'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

const isExpired = (d: string | null | undefined) => !!d && new Date(d) < new Date();
// is_low comes from the server: the store's reorder level when set, else the batch threshold.
const isLow = (i: InventoryItem) => i.is_low ?? i.quantity_available <= i.low_stock_threshold;

const ADJUSTMENT_OPTIONS: Array<{ value: AdjustmentType; label: string }> = [
  { value: 'patient_return', label: 'Patient return (+stock)' },
  { value: 'expired_writeoff', label: 'Expired write-off (-stock)' },
  { value: 'damaged_writeoff', label: 'Damaged write-off (-stock)' },
  { value: 'correction', label: 'Manual correction' },
];

// isAdmin was a prop on the legacy InventoryTab but never actually read in
// its body — the Adjust action is available to any staff member regardless
// of role, both there and here. Not tightened in this migration; a real
// permission change belongs in its own reviewed pass, not folded into it.
export default function InventoryTab() {
  const [adjustingId, setAdjustingId] = useState<number | null>(null);
  const [adjType, setAdjType] = useState<AdjustmentType>('expired_writeoff');
  const [adjQty, setAdjQty] = useState('');
  const [adjReason, setAdjReason] = useState('');
  const [reasonMissing, setReasonMissing] = useState(false);
  const [historyId, setHistoryId] = useState<number | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // A convenience, not the control — the upload route re-checks the grant.
  const { user } = useAuth();
  const canUpload = !!user?.modules?.includes('pharmacy:inventory-upload');
  // Medicine master (G10): same grants as the API's PATCH /medicines/:id.
  const canEditProducts = canUpload || !!user?.modules?.includes('admin:formulary');
  const [productId, setProductId] = useState<number | null>(null);

  // undefined = every store at the branch.
  const [storeId, setStoreId] = useState<number | undefined>(undefined);
  const multipleStores = useHasMultipleStores();
  const inventoryQuery = useInventory({ storeId });
  const adjust = useCreateStockAdjustment();

  const inventory = inventoryQuery.data ?? [];

  const openAdjust = (med: InventoryItem) => {
    setAdjustingId(med.id);
    setAdjType(isExpired(med.expiry_date) ? 'expired_writeoff' : 'patient_return');
    setAdjQty('');
    setAdjReason('');
    setReasonMissing(false);
    adjust.reset();
  };

  const submitAdjust = (med: InventoryItem) => {
    if (!adjQty) return;
    // Every adjustment records why (the server refuses one without a reason).
    if (!adjReason.trim()) { setReasonMissing(true); return; }
    const qty = parseInt(adjQty, 10);
    const isPositive = adjType === 'patient_return' || (adjType === 'correction' && qty > 0);
    const delta = isPositive ? Math.abs(qty) : -Math.abs(qty);
    adjust.mutate(
      { inventory_id: med.id, adjustment_type: adjType, quantity_delta: delta, reason: adjReason.trim() || null },
      {
        onSuccess: (r) => {
          setAdjustingId(null);
          // G15: over the approval limit nothing moved yet — say so.
          if (r && typeof r === 'object' && 'pending' in r && (r as { pending?: boolean }).pending) {
            toast({ title: 'Sent for approval', description: 'Stock changes once someone else approves it.', variant: 'success' });
          }
        },
      }
    );
  };

  const adjustError = adjust.error instanceof ApiError ? adjust.error.message : null;

  return (
    <Card>
      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold">Medicine inventory</div>
        <div className="flex flex-wrap items-center gap-2">
          <StorePicker value={storeId} onChange={setStoreId} />
          {canUpload && (
            <Button variant="ghost" size="sm" onClick={() => setUploadOpen(true)}>⬆ Upload inventory (Excel)</Button>
          )}
        </div>
      </div>
      {uploadOpen && <InventoryUploadDialog onClose={() => setUploadOpen(false)} />}
      <AdjustmentRequestsPanel />
      <ProductSheet medicineId={productId} canEdit={canEditProducts} onClose={() => setProductId(null)} />
      {adjustError && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{adjustError}</div>}
      <Table className="min-w-[820px]">
        <TableHeader>
          <TableRow>{['Medicine', 'Schedule', 'Brand', 'Form', 'Batch', 'Expiry', 'Stock', 'MRP', 'Status', ''].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
        </TableHeader>
        <TableBody>
          {inventoryQuery.isLoading ? (
            <TableRow><TableCell colSpan={10} className="p-8 text-center">Loading…</TableCell></TableRow>
          ) : inventoryQuery.isError ? (
            // Was silently an empty table — indistinguishable from "no stock".
            <TableRow>
              <TableCell colSpan={10} className="p-8 text-center text-danger">
                Could not load stock: {inventoryQuery.error instanceof Error ? inventoryQuery.error.message : 'unknown error'}.{' '}
                <Button variant="ghost" size="sm" onClick={() => inventoryQuery.refetch()}>Try again</Button>
              </TableCell>
            </TableRow>
          ) : inventory.length === 0 ? (
            // Stock is per branch, so an empty list usually means "you are on
            // another branch" — say which one rather than showing nothing.
            <TableRow>
              <TableCell colSpan={10} className="p-8 text-center text-ink-muted">
                No stock recorded at <b>{user?.active_branch?.name ?? 'this branch'}</b>. Stock is kept separately for each
                branch — switch branch at the top of the screen to see another branch&apos;s stock
                {canUpload ? ', or use Upload inventory to add it here' : ''}.
              </TableCell>
            </TableRow>
          ) : (
            inventory.map((med) => {
              const exp = isExpired(med.expiry_date);
              const low = isLow(med);
              return (
                <Fragment key={med.id}>
                  <TableRow style={{ background: exp ? '#FEF2F2' : 'transparent' }}>
                    <TableCell>
                      <button type="button" onClick={() => setProductId(med.medicine_id)}
                        className="text-left font-semibold text-teal hover:underline" title="Product details, stock by store and purchase history">
                        {med.generic_name}
                      </button>
                      <div className="text-[11px] text-ink-muted">{med.strength}</div>
                    </TableCell>
                    <TableCell><ScheduleBadge schedule={med.schedule} /></TableCell>
                    <TableCell className="text-ink-muted">{med.brand_name}</TableCell>
                    <TableCell>{med.dosage_form}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {med.batch_number}
                      {multipleStores && !storeId && <div className="font-sans text-[11px] text-ink-muted">{med.store_name}</div>}
                    </TableCell>
                    <TableCell className={exp ? 'text-danger' : 'text-ink-muted'}>
                      {med.expiry_date ? new Date(med.expiry_date).toLocaleDateString('en-IN') : '—'}
                    </TableCell>
                    <TableCell className={`font-bold ${low ? 'text-danger' : ''}`}>{med.quantity_available}</TableCell>
                    <TableCell>₹{med.mrp}</TableCell>
                    <TableCell><Badge variant={exp ? 'danger' : low ? 'warn' : 'success'}>{exp ? 'Expired' : low ? 'Low stock' : 'OK'}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openAdjust(med)}>Adjust</Button>
                        <Button variant="ghost" size="sm" onClick={() => setHistoryId((h) => (h === med.id ? null : med.id))}>
                          {historyId === med.id ? '▲ History' : '📜 History'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {adjustingId === med.id && (
                    <TableRow>
                      <TableCell colSpan={10} className="bg-canvas p-3">
                        <div className="flex flex-wrap items-end gap-2">
                          <FormField label="Type">
                            <NativeSelect value={adjType} onChange={(e) => setAdjType(e.target.value as AdjustmentType)} className="text-xs">
                              {ADJUSTMENT_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </NativeSelect>
                          </FormField>
                          <FormField label="Quantity">
                            <Input type="number" value={adjQty} onChange={(e) => setAdjQty(e.target.value)} className="w-20 text-xs" />
                          </FormField>
                          <FormField label="Reason" required error={reasonMissing ? 'Enter a reason for this adjustment.' : undefined} className="min-w-[160px] flex-1">
                            <Input value={adjReason} onChange={(e) => { setAdjReason(e.target.value); if (e.target.value.trim()) setReasonMissing(false); }} className="text-xs" placeholder="e.g. Patient returned unused strip" aria-invalid={reasonMissing || undefined} />
                          </FormField>
                          <Button size="sm" disabled={adjust.isPending || !adjQty} onClick={() => submitAdjust(med)}>
                            {adjust.isPending ? '…' : 'Save'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setAdjustingId(null)}>Cancel</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {historyId === med.id && (
                    <TableRow>
                      <TableCell colSpan={10} className="bg-canvas p-0">
                        <InventoryHistoryPanel inventoryId={med.id} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
